/**
 * Eric 2026-06-25 (iter124 — Session A7) — Panneau Procédures.
 *
 * Extrait de VideoDetailPage.js dans le cadre du refacto P0.
 * 2e plus gros panneau du fichier (~630 lignes). Affiche les procédures
 * extraites par le pipeline IA + le mode édition inline + l'import .docx
 * + le mode Notebook (markdown procédural). Gating canEdit/isAdmin côté
 * parent (Vincent #336 SPEC STRICK).
 */
import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog } from "../../lib/uiDialogs";
import { libelleSource } from "../../lib/sourceTranscription";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { CheckCircle, PencilSimple, Trash } from "@phosphor-icons/react";
import { formatTitleHistoryEntry } from "../../lib/knowledgeFormat";
import { formatTime } from "../../lib/videoDetailFormat";
import KUFavoriteHeart from "./KUFavoriteHeart";
import { useFavoriteKuIds } from "../../hooks/useFavoriteKuIds";

export const ProceduresPanel = ({
  show, canEdit, isAdmin, lang, api, videoId,
  video, knowledgeUnits, allTranscripts, pipelineHasEverRun,
  procedures, proceduresSubtab, setProceduresSubtab,
  proceduresKeepPrevious, setProceduresKeepPrevious,
  procDraft, setProcDraft,
  procTitleProposals, setProcTitleProposals,
  procTitleHistory, setProcTitleHistory,
  procTitleGenerating, setProcTitleGenerating,
  setProcClearHistoryConfirm,
  editingProcedureId, setEditingProcedureId,
  notebookProcedureDoc, setNotebookProcedureDoc,
  notebookProcRunning, setNotebookProcRunning,
  importDocxLoading, importDocxFile,
  setDetailModalKu,
  loadVideoData, openReportModal, seekTo,
}) => {
  // Vincent B614 (Eric 2026-07-29 SPEC STRICK) — Set des KUs favorites de
  // l'utilisateur pour afficher le ♥ sur chaque procédure.
  const favKuIds = useFavoriteKuIds();
  if (!show) return null;
  return (
              <div className="flex flex-col" data-testid="procedures-panel">
                {/* Vincent bug #84 (2026-05-08) — Bandeau de statut style "Locuteurs". */}
                {(() => {
                  const procTotal = (knowledgeUnits || []).filter(k => k.type === "procedure" || k.type === "process").length;
                  const procApproved = (knowledgeUnits || []).filter(k => (k.type === "procedure" || k.type === "process") && k.status === "approved").length;
                  const procPending = procTotal - procApproved;
                  // Enhancement B614.d (Eric 2026-07-30) — Compteur ♥ favoris.
                  const procFavorites = (knowledgeUnits || []).filter(k => (k.type === "procedure" || k.type === "process") && favKuIds.has(k.id)).length;
                  return (
                    <div className="px-4 py-2.5 bg-purple-50/50 border-b border-purple-200 flex items-center justify-between gap-3 text-[11px]" data-testid="procedures-status-banner">
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-purple-800">{procTotal} {lang === "fr" ? "procédure(s)" : "procedure(s)"}</span>
                        <span className="text-purple-700">✓ {procApproved} {lang === "fr" ? "révisée(s)" : "reviewed"}</span>
                        {procPending > 0 && <span className="text-amber-700">⏳ {procPending} {lang === "fr" ? "à réviser" : "pending"}</span>}
                        {procFavorites > 0 && (
                          <span className="text-rose-700" data-testid="procedures-favorites-count">♥ {procFavorites} {lang === "fr" ? (procFavorites > 1 ? "favoris" : "favori") : (procFavorites > 1 ? "favorites" : "favorite")}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {procTotal === 0 && !pipelineHasEverRun && (
                          <span className="text-purple-700/80 italic">{lang === "fr" ? "Pipeline non lancé" : "Pipeline not run"}</span>
                        )}
                        {/* Vincent XLSX 2026-07-15 Row 486 (Eric 2026-07-16 SPEC STRICK) —
                            Bouton Lancer/Relancer AFFICHÉ SEULEMENT SI le pipeline n'a
                            jamais été exécuté. Une fois le pipeline lancé, l'utilisateur
                            utilise « Générer à nouveau » dans la pipeline-box. */}
                        {canEdit && !pipelineHasEverRun && (
                          <button
                            onClick={() => setProceduresSubtab("pipeline")}
                            className="px-2 py-1 rounded text-[10px] font-bold bg-purple-600 hover:bg-purple-700 text-white transition-colors"
                            data-testid="procedures-banner-pipeline-shortcut"
                          >
                            ⚗️ {procTotal === 0 ? (lang === "fr" ? "Lancer" : "Run") : (lang === "fr" ? "Relancer" : "Re-run")}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })()}
                {/* Vincent B839 (Eric 2026-02-14 SPEC STRICK PROD) —
                    2 sous-onglets pour TOUS (Pipeline + Modifier/Visualiser).
                    Vincent B848 (Eric 2026-02-14 SPEC STRICK PROD) — Bouton
                    Pipeline masqué pour non-éditeurs (parité 5 panneaux). */}
                <div className="flex border-b border-zinc-200 bg-zinc-50">
                  {canEdit && (
                    <button
                      onClick={() => setProceduresSubtab("pipeline")}
                      className={`flex-1 py-2 text-xs font-medium transition-colors relative ${
                        proceduresSubtab === "pipeline" ? "text-purple-700 bg-white" : "text-zinc-500 hover:text-zinc-700"
                      }`}
                      data-testid="procedures-subtab-pipeline"
                    >
                      ⚗️ {lang === "fr" ? "Pipeline" : "Pipeline"}
                      {proceduresSubtab === "pipeline" && <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-purple-500 rounded-full" />}
                    </button>
                  )}
                  <button
                    onClick={() => setProceduresSubtab(canEdit ? "edit" : "view")}
                    className={`flex-1 py-2 text-xs font-medium transition-colors relative ${
                      (proceduresSubtab === "edit" || proceduresSubtab === "view") ? "text-purple-700 bg-white" : "text-zinc-500 hover:text-zinc-700"
                    }`}
                    data-testid={canEdit ? "procedures-subtab-edit" : "procedures-subtab-view"}
                  >
                    {canEdit
                      ? <>📋 {lang === "fr" ? "Liste des Procédures" : "Procedures list"}</>
                      : <>👁 {lang === "fr" ? "Visualiser" : "View"}</>}
                    {(proceduresSubtab === "edit" || proceduresSubtab === "view") && <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-purple-500 rounded-full" />}
                  </button>
                </div>
                <div className="p-4 space-y-3">
                  {(proceduresSubtab === "pipeline" || proceduresSubtab === "generate") && (() => {
                    const procedures = (knowledgeUnits || []).filter(k => k.type === "procedure" || k.type === "process");
                    const hasRun = procedures.length > 0 || !!notebookProcedureDoc;
                    const lastProcAt = Math.max(0, ...procedures.map(k => new Date(k.created_at || 0).getTime()));
                    const mergedUpdatedAt = new Date(allTranscripts?.merged?.updated_at || allTranscripts?.merged?.created_at || 0).getTime();
                    const transcriptModifiedAfterRun = hasRun && mergedUpdatedAt > 0 && lastProcAt > 0 && mergedUpdatedAt > lastProcAt;
                    return (
                      <div className="space-y-3" data-testid="procedures-pipeline-view">
                        <p className="text-xs text-zinc-600">{lang === "fr"
                          ? "Pipeline Notebook P3 qui génère un document markdown complet : avant-propos, procédures détaillées, tableau synchro 4 colonnes (timecode/verbatim/étape/script TTS). Durée : 2-3 min."
                          : "Notebook P3 pipeline that generates a full markdown document. Duration: 2-3 min."}
                        </p>
                        {transcriptModifiedAfterRun && (
                          <div className="px-3 py-2 rounded border border-amber-300 bg-amber-50 text-[11px] text-amber-900 flex items-start gap-2" data-testid="procedures-transcript-modified-warning">
                            <span>⚠️</span>
                            <span>{lang === "fr"
                              ? "La transcription fusionnée a été modifiée depuis la dernière exécution du pipeline. Cliquez « Générer à nouveau » pour mettre à jour les procédures."
                              : "The merged transcript has been modified since the last pipeline run."}
                            </span>
                          </div>
                        )}
                        {/* Vincent #107 (2026-05-08) — Boîte pâle avec 2 colonnes : Générer (gauche) | Rapport (droite) */}
                        <div className="rounded-lg border border-purple-200 bg-purple-50/60 p-3" data-testid="procedures-pipeline-box">
                          {/* Vincent #23 batch FLHQ (Eric 2026-02-06) — Case à cocher
                              "Conserver les procédures déjà générées" pour relance contrôlée. */}
                          {hasRun && procedures.length > 0 && (
                            <label className="flex items-center gap-2 text-xs text-zinc-700 mb-2" data-testid="procedures-keep-previous-label">
                              <input
                                type="checkbox"
                                checked={proceduresKeepPrevious}
                                onChange={(e) => setProceduresKeepPrevious(e.target.checked)}
                                data-testid="procedures-keep-previous-checkbox"
                                className="rounded"
                              />
                              {lang === "fr"
                                ? `Conserver les ${procedures.length} procédures déjà générées (sinon elles seront supprimées avant relance)`
                                : `Keep existing ${procedures.length} procedures (otherwise delete before relaunch)`}
                            </label>
                          )}
                          {/* Vincent B839 (Eric 2026-02-14 SPEC STRICK PROD)
                              — Grille 2×2 EXACTE :
                                 [ Générer/Régénérer | Effacer ]
                                 [ Rapport           | Importer ] */}
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              /* Vincent B1022 (Eric 2026-02-26 SPEC STRICK PROD)
                                 — Le disabled={notebookProcRunning} laissait le
                                 bouton grisé quand un run précédent restait
                                 marqué "en cours" (B1023 : cas de pipeline
                                 bloqué depuis 19h). Impossible de relancer
                                 sans intervention DB. Fix : jamais grisé,
                                 confirm dialog explicite quand un run est
                                 supposé en cours pour permettre à Vincent
                                 de forcer une relance. */
                              onClick={async () => {
                                if (notebookProcRunning) {
                                  const forceOk = await confirmDialog(lang === "fr"
                                    ? "Un run précédent est marqué comme en cours. Forcer une nouvelle génération ? (utile si le pipeline précédent est bloqué)"
                                    : "A previous run is marked as running. Force a new generation? (useful if previous is stuck)", { tone: "warning" });
                                  if (!forceOk) return;
                                  setNotebookProcRunning(false);
                                }
                                const ok = await confirmDialog(lang === "fr"
                                  ? "Lancer la génération de la procédure complète (prompt Notebook P3) ?\nCela peut prendre 2-3 minutes selon la longueur de la transcription."
                                  : "Run Notebook P3 procedure generation? (2-3 min)", { tone: "warning" });
                                if (!ok) return;
                                // Vincent #23 — Si checkbox décochée, supprimer les procédures existantes avant.
                                if (hasRun && procedures.length > 0 && !proceduresKeepPrevious) {
                                  try {
                                    await axios.delete(`${api}/api/agent/knowledge-units/by-video/${videoId}?type=procedure`, { withCredentials: true });
                                    await axios.delete(`${api}/api/agent/knowledge-units/by-video/${videoId}?type=process`, { withCredentials: true });
                                    toast.info(lang === "fr" ? `${procedures.length} procédures supprimées avant relance.` : `${procedures.length} procedures deleted before relaunch.`);
                                  } catch (e) {
                                    toast.error(`Erreur suppression : ${e.response?.data?.detail || e.message}`);
                                    return;
                                  }
                                }
                                setNotebookProcRunning(true);
                                try {
                                  toast.info(lang === "fr" ? "Génération lancée en arrière-plan (2-3 min)..." : "Background generation started (2-3 min)...");
                                  await axios.post(`${api}/api/videos/${videoId}/generate-procedure-doc`, {}, { withCredentials: true, timeout: 30000 });
                                  let attempts = 0;
                                  const poll = setInterval(async () => {
                                    attempts++;
                                    if (attempts > 60) {
                                      clearInterval(poll);
                                      setNotebookProcRunning(false);
                                      toast.error(lang === "fr" ? "Délai dépassé (5 min). Réessayez." : "Timeout. Retry.");
                                      return;
                                    }
                                    try {
                                      const { data } = await axios.get(`${api}/api/videos/${videoId}/procedure-doc`, { withCredentials: true });
                                      if (data.generation_status === "complete" || data.markdown_content) {
                                        clearInterval(poll);
                                        setNotebookProcRunning(false);
                                        const mdLen = (data.markdown_content || "").trim().length;
                                        if (mdLen === 0) {
                                          toast.warning(lang === "fr"
                                            ? "⚠️ Aucune procédure détectée dans cette transcription."
                                            : "⚠️ No procedure detected in this transcript.",
                                            { duration: 12000 });
                                          return;
                                        }
                                        setNotebookProcedureDoc(data);
                                        toast.success(lang === "fr" ? `✅ Procédure générée (${mdLen} chars)` : `Done (${mdLen} chars)`, { duration: 8000 });
                                        setProceduresSubtab("view");
                                      } else if (data.generation_status === "error") {
                                        clearInterval(poll);
                                        setNotebookProcRunning(false);
                                        toast.error(lang === "fr" ? `Erreur : ${data.generation_error || "génération échouée"}` : `Error`, { duration: 10000 });
                                      }
                                    } catch {
                                      // 404 = doc effacé entretemps, continuer poll
                                    }
                                  }, 5000);
                                } catch (e) {
                                  setNotebookProcRunning(false);
                                  toast.error(`Erreur : ${e.response?.data?.detail || e.message}`, { duration: 10000 });
                                }
                              }}
                              className="px-3 py-2.5 rounded-lg border-2 border-purple-300 bg-white hover:bg-purple-100 text-purple-800 transition-all text-xs font-medium leading-tight disabled:opacity-50"
                              data-testid="generate-notebook-procedure-btn"
                            >
                              {notebookProcRunning ? "⏳ " + (lang === "fr" ? "En cours..." : "Generating...") : "⚗️ " + (lang === "fr"
                                ? (hasRun ? "Générer à nouveau les procédures" : "Générer les procédures")
                                : (hasRun ? "Generate procedures again" : "Generate procedures"))}
                            </button>
                            <button
                              onClick={async () => {
                                // Vincent B648 v2 (Eric 2026-08-01 SPEC STRICK PROD)
                                // — Bouton dédié pour effacer les procédures.
                                const count = (knowledgeUnits || []).filter((k) => k.type === "procedure" || k.type === "process").length;
                                if (!count) {
                                  toast.info(lang === "fr" ? "Aucune procédure à effacer" : "No procedure to clear");
                                  return;
                                }
                                const ok = await confirmDialog(lang === "fr"
                                  ? `Effacer définitivement les ${count} procédure(s) existante(s) ? Cette action est irréversible.`
                                  : `Permanently delete the ${count} existing procedure(s)? This cannot be undone.`);
                                if (!ok) return;
                                try {
                                  const r = await axios.delete(`${api}/api/agent/knowledge-units/by-video/${videoId}?type=procedure`, { withCredentials: true });
                                  toast.success(lang === "fr"
                                    ? `${r?.data?.count || 0} procédure(s) effacée(s)`
                                    : `${r?.data?.count || 0} procedure(s) cleared`);
                                  loadVideoData && loadVideoData();
                                } catch (e) {
                                  toast.error(e.response?.data?.detail || "Erreur suppression procédures");
                                }
                              }}
                              className="px-3 py-2.5 rounded-lg border-2 border-red-300 bg-white hover:bg-red-100 text-red-800 transition-all text-xs font-medium leading-tight"
                              data-testid="clear-procedures-btn"
                              title={lang === "fr" ? "Effacer toutes les procédures de cette vidéo" : "Clear all procedures for this video"}
                            >
                              🗑 {lang === "fr" ? "Effacer les procédures" : "Clear procedures"}
                            </button>
                            <button
                              onClick={() => openReportModal('procedures')}
                              disabled={!hasRun}
                              className="px-3 py-2.5 rounded-lg border-2 border-blue-300 bg-white hover:bg-blue-100 text-blue-800 transition-all text-xs font-medium leading-tight disabled:opacity-40 disabled:cursor-not-allowed"
                              data-testid="procedures-report-btn"
                            >
                              📄 {lang === "fr" ? "Rapport" : "Report"}
                            </button>
                            {/* Vincent #255 (2026-02-06) — Import .docx pour Procédures.
                                B839 : intégré à la grille 2×2 (auparavant sur une ligne séparée). */}
                            <label className="block">
                              <input
                                type="file"
                                accept=".docx"
                                className="hidden"
                                onChange={(e) => importDocxFile(e.target.files?.[0], "procedure")}
                                data-testid="procedures-import-docx-input"
                              />
                              <span
                                className={`block w-full text-center px-3 py-2.5 rounded-lg border-2 border-[#002FA7] bg-white hover:bg-blue-50 text-[#002FA7] transition-all text-xs font-medium leading-tight cursor-pointer ${importDocxLoading ? "opacity-50 cursor-wait" : ""}`}
                                data-testid="procedures-import-docx-btn"
                              >
                                {importDocxLoading
                                  ? (lang === "fr" ? "⏳ Import en cours…" : "⏳ Importing…")
                                  : <>📥 {lang === "fr" ? "Importer Procédures (fichier Word .docx)" : "Import procedures (Word .docx file)"}</>}
                              </span>
                            </label>
                          </div>
                          {/* Tâche 1237 (Eric 2026-06-26) — Sortie Word des
                              procédures : jusqu'ici seules les Q&R pouvaient
                              sortir en .docx, il fallait tout recopier pour
                              relire hors de l'écran. */}
                          <button
                            onClick={() => window.open(
                              `${api}/api/videos/${videoId}/knowledge-report.docx?kind=procedure`,
                              "_blank", "noopener")}
                            disabled={(procedures || []).length === 0}
                            className="mt-2 w-full px-3 py-2.5 rounded-lg border-2 border-purple-300 bg-white hover:bg-purple-50 text-purple-800 transition-all text-xs font-medium leading-tight disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="procedures-report-docx-btn"
                            title={lang === "fr"
                              ? "Télécharger toutes les procédures de cette vidéo dans un document Word"
                              : "Download every procedure of this video as a Word document"}
                          >
                            📝 {lang === "fr" ? "Télécharger en Word" : "Download as Word"}
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                  {/* Vincent B865 (Eric 2026-02-14 SPEC STRICK PROD) —
                      Retire la liste des procédures du sous-onglet
                      « Pipeline » ; désormais visible UNIQUEMENT dans
                      « Liste des Procédures » (edit) et « Visualiser »
                      (view). Le Pipeline garde seulement le bandeau de
                      contrôle (Générer / Rapport / Effacer / Import). */}
                  {(proceduresSubtab === "view" || proceduresSubtab === "edit") && (() => {
                    const procedures = (knowledgeUnits || []).filter(k => k.type === "procedure" || k.type === "process");
                    // Vincent (Eric 2026-05-13) — Parité avec l'onglet 4. Scripts.
                    // La liste des procédures est désormais visible dans les 3 sous-onglets :
                    //  - Pipeline : liste + génération (haut)
                    //  - Visualiser : liste seule, read-only (lecture rapide pour QA)
                    //  - Modifier : liste avec tous les boutons d'édition (mode admin)
                    const isReadOnly = proceduresSubtab === "view";
                    // Vincent 2026-05-06 (bug #64) — afficher le notebook doc s'il existe MEME si pas de procédures atomiques
                    if (procedures.length === 0 && !notebookProcedureDoc) {
                      return (
                        <div className="rounded-lg border border-dashed border-zinc-300 p-6 text-center bg-zinc-50" data-testid="procedures-empty-state">
                          <p className="text-sm text-zinc-600 mb-2">
                            {lang === "fr" ? "Aucune procédure pour cette vidéo." : "No procedures yet."}
                          </p>
                          <p className="text-xs text-zinc-500">
                            {lang === "fr"
                              ? "Cliquez sur « Générer » pour produire une procédure complète Notebook."
                              : "Click « Generate » to produce a Notebook procedure."}
                          </p>
                        </div>
                      );
                    }
                    return (
                      <div className="space-y-3" data-testid="procedures-list">
                        {/* Vincent 2026-05-06 (bug #64) — Document procédure markdown complet.
                            Eric / Vincent (2026-05-14) — Replié par défaut : les cartes individuelles
                            (procédures atomiques) sont la vue principale ; le document complet reste
                            accessible en un clic pour Vincent (tableau 4 colonnes + avant-propos). */}
                        {/* Vincent #355 (Eric 2026-06-22 SPEC STRICK 3e passe) — Non-éditeurs
                            ne voient pas le "Document complet (Notebook P3)" : c'est un
                            artefact de génération réservé aux concepteurs/admins/founders. */}
                        {notebookProcedureDoc && canEdit && (
                          <details className="rounded-lg border border-blue-200 bg-blue-50/40 p-3 mb-3" data-testid="notebook-procedure-doc">
                            <summary className="cursor-pointer text-sm font-semibold text-blue-900">
                              📝 {lang === "fr" ? "Document complet (Notebook P3)" : "Full document (Notebook P3)"}
                              <span className="text-xs font-normal text-blue-700 ml-2">
                                ({libelleSource(notebookProcedureDoc.transcript_source, lang)} · {notebookProcedureDoc.markdown_content?.length || 0} {lang === "fr" ? "car." : "chars"} · {new Date(notebookProcedureDoc.created_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA")})
                              </span>
                            </summary>
                            <div className="mt-3 prose prose-sm max-w-none bg-white rounded p-3 border border-blue-200 max-h-[600px] overflow-y-auto" data-testid="notebook-procedure-rendered">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>{notebookProcedureDoc.markdown_content}</ReactMarkdown>
                            </div>
                            <div className="mt-2 flex gap-2">
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(notebookProcedureDoc.markdown_content);
                                  toast.success(lang === "fr" ? "Markdown copié" : "Markdown copied");
                                }}
                                className="text-xs px-3 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white"
                                data-testid="copy-notebook-md-btn"
                              >
                                📋 {lang === "fr" ? "Copier markdown" : "Copy markdown"}
                              </button>
                              <button
                                onClick={() => window.open(
                                  `${api}/api/videos/${videoId}/knowledge-report.docx?kind=procedure&full=1`,
                                  "_blank", "noopener")}
                                className="text-xs px-3 py-1 rounded bg-zinc-600 hover:bg-zinc-700 text-white"
                                data-testid="download-notebook-docx-btn"
                                title={lang === "fr"
                                  ? "Télécharger le rapport complet en Word (.docx)"
                                  : "Download the full report as Word (.docx)"}
                              >
                                📝 {lang === "fr" ? "Télécharger en Word" : "Download as Word"}
                              </button>
                            </div>
                          </details>
                        )}

                        {procedures.length > 0 && (
                          <div className="space-y-2" data-testid="procedures-editable-list">
                            <p className="text-xs text-zinc-700 font-medium">
                              {procedures.length} {lang === "fr" ? "procédures atomiques extraites" : "atomic procedures"}
                            </p>
                            {procedures.map((p) => {
                              const isPending = p.status === "pending_review";
                              const isApproved = p.status === "approved";
                              const isRejected = p.status === "rejected";
                              const isEditing = editingProcedureId === p.id;
                              // Vincent bug #81 + #82 (2026-05-07) — alignement avec ScriptsPage :
                              // statut badge + boutons éditer/accepter/refuser/supprimer.
                              return (
                                <div
                                  key={p.id}
                                  onClick={(e) => {
                                    // Vincent B699 XLS 2026-08-10 (Eric 2026-02-10)
                                    // — Uniformisation avec Base de
                                    // connaissances / Q&R / Concepts /
                                    // Scripts : un clic ouvre le
                                    // KnowledgeDetailModal partagé (avec
                                    // Précédent/Suivant intégrés). Le
                                    // seek vidéo reste disponible via
                                    // le badge timestamp ou l'onglet
                                    // Pipeline. Ancien comportement
                                    // (seek au clic global) laissait
                                    // les procédures orphelines de la
                                    // nav Prev/Next.
                                    if (isEditing) return;
                                    if (e.target.closest('button, input, textarea, a, [data-no-seek]')) return;
                                    if (typeof setDetailModalKu === "function") {
                                      setDetailModalKu(p);
                                    }
                                  }}
                                  className={`p-3 rounded-lg border border-zinc-200 bg-white hover:shadow-sm transition-all ${isEditing ? "" : "cursor-pointer hover:border-purple-300 hover:bg-purple-50/30"}`}
                                  data-testid={`procedure-row-${p.id}`}
                                  data-focus-target={p.id}
                                >
                                  <div className="flex items-start gap-2">
                                    {/* Vincent B614 (Eric 2026-07-29 SPEC STRICK) —
                                        ♥ favori à gauche de chaque procédure. */}
                                    <KUFavoriteHeart
                                      unitId={p.id}
                                      initialIsFavorite={favKuIds.has(p.id)}
                                      testIdPrefix="fav-procedure-btn"
                                      className="mt-0.5"
                                    />
                                    <div className="flex-1 min-w-0">
                                      {isEditing ? (
                                        <div className="space-y-1.5">
                                          <div className="flex items-center gap-2">
                                            <input
                                              value={procDraft.title}
                                              onChange={(e) => setProcDraft(d => ({ ...d, title: e.target.value }))}
                                              className="flex-1 text-sm font-semibold px-2 py-1 border border-blue-300 rounded"
                                              placeholder={lang === "fr" ? "Titre" : "Title"}
                                              data-testid={`proc-edit-title-${p.id}`}
                                            />
                                            {/* Vincent (2026-05-12) — Bouton génération auto de titre (parité Q&R) */}
                                            <button
                                              type="button"
                                              disabled={procTitleGenerating}
                                              onClick={async () => {
                                                if (procTitleGenerating) return;
                                                setProcTitleGenerating(true);
                                                try {
                                                  const { data } = await axios.post(
                                                    `${api}/api/agent/knowledge-units/${p.id}/generate-title`,
                                                    {},
                                                    { withCredentials: true, timeout: 30000 }
                                                  );
                                                  setProcTitleProposals(Array.isArray(data?.proposals) ? data.proposals : (data?.title ? [data.title] : []));
                                                  setProcTitleHistory(Array.isArray(data?.history) ? data.history : []);
                                                  const firstTitle = (data?.proposals && data.proposals[0]) || data?.title;
                                                  if (firstTitle) {
                                                    setProcDraft(d => ({ ...d, title: firstTitle }));
                                                    toast.success(lang === "fr"
                                                      ? `${(data?.proposals || []).length || 1} proposition(s) générée(s)`
                                                      : `${(data?.proposals || []).length || 1} suggestion(s)`);
                                                  }
                                                } catch (err) {
                                                  toast.error(err.response?.data?.detail || (lang === "fr" ? "Échec de la génération" : "Génération failed"));
                                                } finally {
                                                  setProcTitleGenerating(false);
                                                }
                                              }}
                                              className="px-2 py-1 text-[11px] rounded bg-purple-600 hover:bg-purple-700 text-white font-medium whitespace-nowrap disabled:opacity-60"
                                              data-testid={`proc-generate-title-${p.id}`}
                                              title={lang === "fr" ? "Générer 3 propositions de titre via IA" : "Generate 3 AI title suggestions"}
                                            >
                                              {procTitleGenerating ? "⏳" : "🪄"} {lang === "fr" ? "Titre auto" : "Auto title"}
                                            </button>
                                          </div>
                                          {/* Propositions cliquables */}
                                          {procTitleProposals.length > 0 && (
                                            <div className="flex flex-wrap gap-1.5" data-testid={`proc-title-proposals-${p.id}`}>
                                              {procTitleProposals.map((t, ti) => (
                                                <button
                                                  key={ti}
                                                  type="button"
                                                  onClick={() => setProcDraft(d => ({ ...d, title: t }))}
                                                  className={`text-[10.5px] px-2 py-0.5 rounded border ${procDraft.title === t ? "bg-purple-100 border-purple-400 text-purple-900 font-semibold" : "bg-white border-zinc-300 hover:border-purple-300 hover:bg-purple-50 text-zinc-700"}`}
                                                  data-testid={`proc-title-proposal-${p.id}-${ti}`}
                                                >
                                                  {t}
                                                </button>
                                              ))}
                                            </div>
                                          )}
                                          {/* Historique (titres antérieurs) — restaurable + effaçable */}
                                          {procTitleHistory.length > 0 && (
                                            <details className="text-[10.5px] text-zinc-600" data-testid={`proc-title-history-${p.id}`}>
                                              <summary className="cursor-pointer inline-flex items-center gap-1 hover:text-zinc-900">
                                                ↺ {lang === "fr" ? "Historique" : "History"} ({procTitleHistory.length})
                                              </summary>
                                              <div className="mt-1 ml-2 space-y-0.5">
                                                {procTitleHistory.slice().reverse().map((h, hi) => (
                                                  <div key={hi} className="flex items-center gap-2">
                                                    <button
                                                      type="button"
                                                      onClick={() => {
                                                        setProcDraft(d => ({ ...d, title: h.title }));
                                                        toast.info(lang === "fr" ? "Titre restauré (sauvegardez pour confirmer)" : "Title restored (save to confirm)");
                                                      }}
                                                      className="text-blue-600 hover:underline text-left flex-1"
                                                      data-testid={`proc-title-restore-${p.id}-${hi}`}
                                                      title={lang === "fr" ? "Revenir à ce titre" : "Revert to this title"}
                                                    >
                                                      {/* Vincent 20/05/2026 — Format "AAAA-MM-JJ HH:MM : titre" */}
                                                      {h.replaced_at && (
                                                        <span className="text-zinc-500 font-mono text-[10px] mr-1">
                                                          {formatTitleHistoryEntry(h.replaced_at)} :
                                                        </span>
                                                      )}
                                                      <span>{h.title}</span>
                                                    </button>
                                                  </div>
                                                ))}
                                                <button
                                                  type="button"
                                                  onClick={() => setProcClearHistoryConfirm({ procId: p.id, title: p.title })}
                                                  className="text-[10px] text-rose-600 hover:text-rose-800 mt-1"
                                                  data-testid={`proc-title-history-clear-${p.id}`}
                                                >
                                                  🗑 {lang === "fr" ? "Effacer l'historique" : "Clear history"}
                                                </button>
                                              </div>
                                            </details>
                                          )}
                                          <textarea
                                            value={procDraft.summary}
                                            onChange={(e) => setProcDraft(d => ({ ...d, summary: e.target.value }))}
                                            rows={Math.max(5, (procDraft.summary || "").split("\n").length + 1)}
                                            className="w-full text-xs px-2 py-1 border border-zinc-300 rounded resize-y font-mono"
                                            placeholder={lang === "fr" ? "Étapes (une par ligne) ou résumé libre" : "Steps (one per line) or free-form summary"}
                                            data-testid={`proc-edit-summary-${p.id}`}
                                          />
                                          <p className="text-[10px] text-zinc-500 italic mt-0.5">
                                            {lang === "fr"
                                              ? "💡 Chaque ligne devient une étape numérotée. Lignes vides ignorées."
                                              : "💡 Each line becomes a numbered step. Blank lines are ignored."}
                                          </p>
                                        </div>
                                      ) : (
                                        <>
                                          <div className="flex items-center gap-2 flex-wrap">
                                            <p className="text-sm font-medium text-zinc-900">{p.title}</p>
                                            {/* Eric / Vincent (2026-05-14) — Badge "Étapes : N" pour visualiser
                                                d'un coup d'œil les procédures courtes vs longues. */}
                                            {Array.isArray(p.content?.steps) && p.content.steps.length > 0 && (
                                              <span
                                                className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-purple-100 text-purple-700"
                                                data-testid={`proc-steps-badge-${p.id}`}
                                                title={lang === "fr" ? `${p.content.steps.length} étape(s)` : `${p.content.steps.length} step(s)`}
                                              >
                                                🎯 {p.content.steps.length}
                                              </span>
                                            )}
                                          </div>
                                          {p.content?.summary && <p className="text-xs text-zinc-600 mt-1">{p.content.summary}</p>}
                                          {/* Vincent #134 (Eric 2026-05-13) — Affichage des étapes inline.
                                              Les procédures stockent leur contenu dans `content.steps`
                                              (tableau de chaînes). Sans cet affichage, la liste paraît vide
                                              car les KU n'ont quasi jamais de champ `summary`. */}
                                          {Array.isArray(p.content?.steps) && p.content.steps.length > 0 && (
                                            <ol className="list-decimal list-inside text-xs text-zinc-700 mt-1.5 space-y-0.5" data-testid={`proc-steps-${p.id}`}>
                                              {p.content.steps.map((s, si) => (
                                                <li key={si} className="leading-snug">
                                                  {typeof s === "string" ? s : (s?.text || JSON.stringify(s))}
                                                </li>
                                              ))}
                                            </ol>
                                          )}
                                          {p.timestamps?.start != null && (
                                            <p className="text-[10px] font-mono text-zinc-400 mt-1.5">[{formatTime(p.timestamps.start)}]</p>
                                          )}
                                        </>
                                      )}
                                    </div>
                                    {/* Vincent 2026-06-01 — Badge statut GAUCHE retiré
                                        (doublon avec le badge à droite après les boutons).
                                        Seul le badge de droite (status-procedure-*) est conservé. */}
                                    {isAdmin && (
                                      isEditing ? (
                                        <>
                                          <button
                                            onClick={async (e) => {
                                              e.stopPropagation();
                                              const payload = {};
                                              if (procDraft.title !== p.title) payload.title = procDraft.title;
                                              // Eric / Vincent (2026-05-14) — Si la procédure avait des
                                              // `steps`, on les reconstruit en splittant les lignes du
                                              // textarea (chaque ligne non vide = une étape). Sinon, on
                                              // sauvegarde dans `summary` (compat ascendante).
                                              if (procDraft.hadSteps) {
                                                const lines = (procDraft.summary || "")
                                                  .split("\n")
                                                  .map(s => s.trim())
                                                  .filter(Boolean);
                                                const prevSteps = (p.content?.steps || []).map(s => typeof s === "string" ? s : (s?.text || ""));
                                                if (JSON.stringify(lines) !== JSON.stringify(prevSteps)) {
                                                  payload.steps = lines;
                                                }
                                              } else if (procDraft.summary !== (p.content?.summary || "")) {
                                                payload.summary = procDraft.summary;
                                              }
                                              if (Object.keys(payload).length === 0) {
                                                setEditingProcedureId(null);
                                                return;
                                              }
                                              try {
                                                await axios.patch(`${api}/api/agent/knowledge-units/${p.id}`, payload, { withCredentials: true });
                                                toast.success(lang === "fr" ? "Procédure mise à jour" : "Procedure updated");
                                                setEditingProcedureId(null);
                                                loadVideoData();
                                              } catch (err) { toast.error(`Erreur : ${err.response?.data?.detail || err.message}`); }
                                            }}
                                            className="text-xs text-teal-600 hover:bg-teal-50 px-1.5 py-0.5 rounded"
                                            data-testid={`proc-save-${p.id}`}
                                          >💾</button>
                                          <button
                                            onClick={(e) => { e.stopPropagation(); setEditingProcedureId(null); }}
                                            className="text-xs text-zinc-500 hover:bg-zinc-100 px-1.5 py-0.5 rounded"
                                            data-testid={`proc-cancel-${p.id}`}
                                          >✕</button>
                                        </>
                                      ) : (
                                        <>
                                          {!isReadOnly && (
                                          <button
                                            onClick={async (e) => {
                                              e.stopPropagation();
                                              setEditingProcedureId(p.id);
                                              // Eric / Vincent (2026-05-14) — Si la procédure stocke ses
                                              // étapes dans `content.steps`, on les présente sous forme
                                              // d'une étape par ligne dans le textarea. Sinon, on
                                              // retombe sur le `summary` libre (compat ascendante).
                                              const hasSteps = Array.isArray(p.content?.steps) && p.content.steps.length > 0;
                                              const initialBody = hasSteps
                                                ? p.content.steps.map((s) => (typeof s === "string" ? s : (s?.text || JSON.stringify(s)))).join("\n")
                                                : (p.content?.summary || "");
                                              setProcDraft({ title: p.title || "", summary: initialBody, hadSteps: hasSteps });
                                              // Vincent (2026-05-12) — reset puis charger l'historique pour cette procédure
                                              setProcTitleProposals([]);
                                              setProcTitleHistory([]);
                                              try {
                                                const { data } = await axios.get(`${api}/api/agent/knowledge-units/${p.id}/history`, { withCredentials: true });
                                                const editHistory = data?.edit_history || [];
                                                const titleEntries = editHistory
                                                  .filter(e2 => (e2.fields_changed || []).includes("title") && (e2.previous_title || "").trim())
                                                  .map(e2 => ({ title: e2.previous_title, replaced_at: e2.edited_at, replaced_by_email: e2.edited_by_email }));
                                                setProcTitleHistory(titleEntries.slice(-20));
                                              } catch { /* historique optionnel */ }
                                            }}
                                            className="text-xs text-blue-600 hover:bg-blue-50 px-1.5 py-0.5 rounded inline-flex items-center"
                                            data-testid={`edit-procedure-${p.id}`}
                                            title={lang === "fr" ? "Éditer inline" : "Edit inline"}
                                          ><PencilSimple size={14} weight="bold" /></button>
                                          )}
                                          {/* Vincent XLS iter103 (item 18) — Bouton "Vue KB" qui ouvre
                                              le KnowledgeDetailModal partagé avec la Base de connaissances.
                                              Donne la MÊME UI/UX que pour les Q&R/Scripts/Concepts.
                                              L'édition inline reste disponible pour les power users. */}
                                          <button
                                            onClick={(e) => { e.stopPropagation(); setDetailModalKu(p); }}
                                            className="text-xs text-purple-600 hover:bg-purple-50 px-1.5 py-0.5 rounded inline-flex items-center"
                                            data-testid={`view-procedure-kb-${p.id}`}
                                            title={lang === "fr" ? "Ouvrir la vue Base de connaissances (mêmes boutons)" : "Open Knowledge Base view"}
                                          >🔍</button>
                                          {!isReadOnly && isPending && (
                                            <button
                                              onClick={async (e) => {
                                                e.stopPropagation();
                                                try {
                                                  await axios.post(`${api}/api/agent/draft-queue/${p.id}/decide`, { decision: "approve" }, { withCredentials: true });
                                                  toast.success(lang === "fr" ? "Procédure validée" : "Procedure validated");
                                                  loadVideoData();
                                                } catch (err) { toast.error(`Erreur : ${err.response?.data?.detail || err.message}`); }
                                              }}
                                              className="text-xs text-teal-600 hover:bg-teal-50 px-1.5 py-0.5 rounded inline-flex items-center"
                                              data-testid={`approve-procedure-${p.id}`}
                                              title={lang === "fr" ? "Valider" : "Validate"}
                                            ><CheckCircle size={14} weight="bold" /></button>
                                          )}
                                          {!isReadOnly && (
                                            <button
                                              onClick={async (e) => {
                                                e.stopPropagation();
                                                const confirmMsg = isPending
                                                  ? (lang === "fr" ? "Refuser cette procédure ?" : "Reject this procedure?")
                                                  : (lang === "fr" ? "Supprimer cette procédure ?" : "Delete this procedure?");
                                                const ok = await confirmDialog(confirmMsg);
                                                if (!ok) return;
                                                try {
                                                  if (isPending) {
                                                    await axios.post(`${api}/api/agent/draft-queue/${p.id}/decide`, { decision: "reject" }, { withCredentials: true });
                                                    toast.success(lang === "fr" ? "Procédure refusée" : "Procedure rejected");
                                                  } else {
                                                    await axios.delete(`${api}/api/agent/knowledge-units/${p.id}`, { withCredentials: true });
                                                    toast.success(lang === "fr" ? "Procédure supprimée" : "Procedure deleted");
                                                  }
                                                  loadVideoData();
                                                } catch (err) { toast.error(`Erreur : ${err.response?.data?.detail || err.message}`); }
                                              }}
                                              className="text-xs text-red-500 hover:bg-red-50 px-1.5 py-0.5 rounded inline-flex items-center"
                                              data-testid={isPending ? `reject-procedure-${p.id}` : `delete-procedure-${p.id}`}
                                              title={isPending ? (lang === "fr" ? "Refuser" : "Reject") : (lang === "fr" ? "Supprimer" : "Delete")}
                                            ><Trash size={14} weight="bold" /></button>
                                          )}
                                          {/* Vincent #202 — Badge statut procédure */}
                                          <span
                                            className={`text-xs px-1.5 py-0.5 rounded inline-flex items-center font-bold ${
                                              isPending
                                                ? "bg-amber-100 text-amber-700"
                                                : "bg-teal-100 text-teal-700"
                                            }`}
                                            title={isPending ? (lang === "fr" ? "En attente de validation" : "Pending validation") : (lang === "fr" ? "Validé" : "Validated")}
                                            data-testid={`status-procedure-${p.id}`}
                                          >
                                            {isPending ? "..." : <CheckCircle size={12} weight="fill" />}
                                          </span>
                                        </>
                                      )
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {!notebookProcedureDoc && procedures.length === 0 && (
                          <div className="text-xs text-zinc-500 italic" data-testid="procedures-empty">
                            {/* Vincent B838 (Eric 2026-02-14 SPEC STRICK PROD) —
                                Message allégé pour les non-éditeurs (pas de CTA
                                « Cliquez sur l'onglet Générer » puisqu'ils ne
                                voient pas ce bouton). */}
                            {lang === "fr"
                              ? (canEdit
                                  ? "Aucune procédure pour cette vidéo. Cliquez sur l'onglet « Générer »."
                                  : "Aucune procédure détectée pour le moment.")
                              : (canEdit
                                  ? "No procedures yet. Click the 'Generate' tab."
                                  : "No procedures detected yet.")}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
  );
};

export default ProceduresPanel;
